<script setup>
import { computed, ref, watch } from 'vue';
import {
  MoreHorizontalSquare01Icon,
  Timer02Icon,
} from '@hugeicons/core-free-icons';
import {
  differenceInCalendarDays,
  format,
  fromUnixTime,
  isSameYear,
  isToday,
  isYesterday,
} from 'date-fns';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import MessagePreview from './MessagePreview.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import InboxChannelGlyph from 'dashboard/routes/dashboard/settings/inbox/components/InboxChannelGlyph.vue';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

// Reset UI state when conversation changes at same index (no :key, instance reused on reorder)
// This prevents context menu/hover state from leaking to a different conversation
// Emit contextMenuToggle(false) to sync parent state if menu was open during recycling
const resetState = () => {
  if (showContextMenu.value) {
    emit('contextMenuToggle', false);
  }
  hovered.value = false;
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

watch(() => props.chat.id, resetState);

const currentChat = useMapGetter('getSelectedChat');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});
const assigneeAvatarSrc = computed(
  () => assignee.value.thumbnail || assignee.value.avatar_url || ''
);
const assigneeCardName = computed(() => {
  const a = assignee.value;
  if (!a?.id) return '';
  return a.name || a.available_name || a.email || `#${a.id}`;
});
const assigneeDisplayName = computed(() => {
  const a = assignee.value;
  if (!a?.id) return t('CONVERSATION.UNASSIGNED');
  return a.name || a.available_name || a.email || `#${a.id}`;
});
const isResolved = computed(() =>
  ['resolved', 'closed'].includes(props.chat.status)
);
const isSnoozed = computed(() => props.chat.status === 'snoozed');

const sender = computed(() => chatMetadata.value.sender || {});
const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  if (!senderId.value) {
    return sender.value;
  }

  return {
    ...sender.value,
    ...store.getters['contacts/getContact'](senderId.value),
  };
});

const lifecycleStage = computed(() => {
  return (
    currentContact.value.lifecycleStage ||
    currentContact.value.lifecycle_stage ||
    sender.value.lifecycleStage ||
    sender.value.lifecycle_stage
  );
});

const selectedLifecycleStageId = computed(
  () =>
    currentContact.value.lifecycleStageId ||
    currentContact.value.lifecycle_stage_id ||
    currentContact.value.lifecycleStage?.id ||
    currentContact.value.lifecycle_stage?.id ||
    sender.value.lifecycleStageId ||
    sender.value.lifecycle_stage_id ||
    sender.value.lifecycleStage?.id ||
    sender.value.lifecycle_stage?.id ||
    null
);

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);

const hasUnread = computed(() => unreadCount.value > 0);
const unreadCountLabel = computed(() =>
  unreadCount.value > 9 ? '9+' : unreadCount.value
);

const conversationTimestamp = computed(() =>
  fromUnixTime(props.chat.timestamp || props.chat.created_at)
);

const formattedConversationTimestamp = computed(() => {
  const timestamp = conversationTimestamp.value;
  const now = new Date();

  if (isToday(timestamp)) {
    return format(timestamp, 'h:mm a');
  }

  if (isYesterday(timestamp)) {
    return 'Yesterday';
  }

  const dayDifference = differenceInCalendarDays(now, timestamp);

  if (dayDifference < 7) {
    return format(timestamp, 'EEE');
  }

  if (!isSameYear(timestamp, now)) {
    return format(timestamp, 'MMM d, yyyy');
  }

  return format(timestamp, 'MMM d');
});

const conversationTimestampTooltip = computed(() =>
  format(conversationTimestamp.value, 'MMM d, yyyy, h:mm a')
);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const showMetaSection = computed(() => props.chat.priority);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => [
  hasUnread.value ? 'font-bold' : 'font-normal',
  'text-black dark:text-n-slate-12',
]);

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenuAt = ({ x, y }) => {
  if (!props.enableContextMenu) return;
  emit('contextMenuToggle', true);
  contextMenu.value.x = x;
  contextMenu.value.y = y;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const toggleContextMenuFromButton = e => {
  if (!props.enableContextMenu) return;

  e.preventDefault();
  e.stopPropagation();

  if (showContextMenu.value) {
    closeContextMenu();
    return;
  }

  const rect = e.currentTarget.getBoundingClientRect();
  openContextMenuAt({
    x: rect.right + 8,
    y: rect.top + 4,
  });
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [props.chat.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full rounded-xl border border-transparent cursor-pointer conversation transition-colors duration-150 hover:bg-n-brand/5 group"
    :class="{
      'active animate-card-select bg-n-brand/10 border-n-brand/20 shadow-sm':
        isActiveChat,
      'bg-n-brand/5': selected && !isActiveChat,
      'px-0': compact,
      'ml-1 mr-1 my-1 px-2 py-0': !compact,
    }"
    @click="onCardClick"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <div v-if="!hideThumbnail" class="relative mt-2.5">
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
          rounded-full
        >
          <template #overlay="{ size }">
            <label
              v-if="hovered || selected"
              class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
              :style="{ width: `${size}px`, height: `${size}px` }"
              @click.stop
            >
              <input
                :value="selected"
                :checked="selected"
                class="!m-0 cursor-pointer"
                type="checkbox"
                @change="onSelectConversation($event.target.checked)"
              />
            </label>
          </template>
        </Avatar>
        <span
          class="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full border border-n-background bg-n-background text-n-brand"
        >
          <InboxChannelGlyph :inbox="inbox" size="xxs" />
        </span>
      </div>
    </div>
    <div class="flex min-w-0 flex-1 flex-col gap-0.5 px-2 py-1.5">
      <!-- Row 1: avatar (sibling) + name + time + options — name row aligns with avatar via outer flex -->
      <div class="flex min-w-0 items-center gap-2">
        <CardPriorityIcon
          v-if="showMetaSection"
          :priority="chat.priority"
          class="size-3.5 shrink-0"
        />
        <h4
          class="conversation--user my-0 min-w-0 flex-1 truncate pt-0.5 text-sm capitalize text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
        >
          {{ currentContact.name }}
        </h4>
        <div class="flex shrink-0 items-center gap-1">
          <span
            v-if="!showContextMenu"
            v-tooltip.top="{
              content: conversationTimestampTooltip,
              delay: { show: 300, hide: 0 },
            }"
            class="flex h-7 items-center whitespace-nowrap text-xs font-medium leading-4 text-n-slate-10 hover:text-n-slate-11"
            :class="enableContextMenu ? 'group-hover:hidden' : ''"
          >
            {{ formattedConversationTimestamp }}
          </span>
          <button
            v-if="enableContextMenu"
            type="button"
            class="flex size-7 items-center justify-center rounded-md text-n-slate-12 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            :class="showContextMenu ? '' : 'hidden group-hover:flex'"
            :aria-label="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
            @click="toggleContextMenuFromButton"
          >
            <HugeIcon
              :icon="MoreHorizontalSquare01Icon"
              :size="20"
              class="shrink-0"
            />
          </button>
        </div>
      </div>
      <!-- Row 2: last message + labels + unread count (single line) -->
      <div class="flex min-h-6 min-w-0 items-center gap-2">
        <div class="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          <div class="min-w-0 flex-1 overflow-hidden">
            <VoiceCallStatus
              v-if="voiceCallData.status"
              key="voice-status-row"
              :status="voiceCallData.status"
              :direction="voiceCallData.direction"
              :message-preview-class="messagePreviewClass"
            />
            <MessagePreview
              v-else-if="lastMessageInChat"
              key="message-preview"
              :message="lastMessageInChat"
              class="my-0 h-6 w-full text-sm leading-6"
              :class="messagePreviewClass"
            />
            <p
              v-else
              key="no-messages"
              data-chat-message-font
              class="my-0 h-6 w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-6 text-n-slate-11"
              :class="messagePreviewClass"
            >
              <fluent-icon
                size="16"
                class="-mt-0.5 inline-block align-middle text-n-slate-10"
                icon="info"
              />
              <span class="mx-0.5">
                {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
              </span>
            </p>
          </div>
          <CardLabels
            v-if="showLabelsSection"
            :conversation-labels="chat.labels"
            class="max-w-[min(12rem,45%)] min-w-0 shrink"
          >
            <template v-if="hasSlaPolicyId" #before>
              <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
            </template>
          </CardLabels>
        </div>
        <span
          v-if="hasUnread"
          class="flex size-5 shrink-0 items-center justify-center rounded-full bg-n-teal-9 text-[10px] font-semibold leading-none text-white shadow-lg"
        >
          {{ unreadCountLabel }}
        </span>
      </div>
      <!-- Row 3: lifecycle + status icons (snooze / resolved) + assignee -->
      <div
        v-if="lifecycleStage || isSnoozed || isResolved || showAssignee"
        class="flex min-h-5 min-w-0 items-center justify-between gap-2"
      >
        <div class="min-w-0 flex-1">
          <span
            v-if="lifecycleStage"
            class="inline-flex max-w-full items-center gap-1 rounded bg-white px-1.5 py-0.5 text-xs text-n-slate-12 outline outline-1 outline-n-weak"
          >
            <span>{{ lifecycleStage.icon }}</span>
            <span class="truncate">{{ lifecycleStage.name }}</span>
          </span>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <span
            v-if="isSnoozed"
            v-tooltip.top="{
              content: $t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
              delay: { show: 200, hide: 0 },
            }"
            class="inline-flex size-5 items-center justify-center text-n-amber-10"
          >
            <HugeIcon :icon="Timer02Icon" :size="20" class="shrink-0" />
          </span>
          <span
            v-if="isResolved"
            v-tooltip.top="{
              content: $t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
              delay: { show: 200, hide: 0 },
            }"
            class="inline-flex size-5 items-center justify-center rounded-full bg-n-teal-9 text-white"
          >
            <span class="i-lucide-check size-4 shrink-0" />
          </span>
          <span
            v-if="showAssignee"
            v-tooltip.top="{
              content: assigneeDisplayName,
              delay: { show: 200, hide: 0 },
            }"
            class="inline-flex shrink-0"
          >
            <Avatar
              v-if="assignee.id"
              :name="assigneeCardName"
              :src="assigneeAvatarSrc"
              :size="20"
              hide-offline-status
              rounded-full
            />
            <span
              v-else
              class="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-n-ruby-4/80"
            >
              <span class="i-lucide-user-round size-4 shrink-0 text-n-ruby-9" />
            </span>
          </span>
        </div>
      </div>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :contact-id="senderId"
        :assignee="assignee"
        :lifecycle-stage-id="selectedLifecycleStageId"
        :priority="chat.priority"
        :conversation-labels="chat.labels"
        :allowed-options="allowedContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>
